import { createContext, useContext, type ReactNode } from "react";

/**
 * The Play launch seam (docs/lifecycle-spine/PRD.md §9 · docs/play/PRD.md).
 *
 * Play is a global experience — the top-nav button opens it, but so should any
 * surface that wants to hand the user the guided next-best-action walk. The most
 * load-bearing case is the Pulse spine: when a function band isn't filled out
 * (an uninstrumented band — all ghosts), its "Set it up" CTA should LAUNCH Play
 * focused on setting that section up, instead of dead-ending on a generic page.
 *
 * The wall lives in `@excellent/ui` and can't import shell code, so the launcher
 * is a context the APP supplies (the same shape as the shell's other chrome
 * providers — e.g. the dock/scope contexts).
 * `usePlayLaunch()` returns a safe no-op (`available:false`) when no provider is
 * mounted (a standalone embed / the styleguide) so the consumer degrades to its
 * own fallback affordance (e.g. a link) instead of throwing.
 */

export interface PlayLaunchOptions {
  /** Open the driver straight onto this action key (skips the intro launcher). */
  startActionKey?: string;
  /** A section to focus setup on — the host maps it to that section's setup card
   *  (`onboarding:setup-<slug>`) when `startActionKey` is not given. A slug that
   *  isn't an open setup card falls back to the top of the ranked queue. */
  sectionSlug?: string;
}

export interface PlayLaunchValue {
  /** Open the Play driver (optionally focused — see PlayLaunchOptions). */
  launch: (opts?: PlayLaunchOptions) => void;
  /** True when a real launcher is mounted; false ⇒ `launch` is a no-op and the
   *  caller should fall back to its own affordance (e.g. a link). */
  available: boolean;
}

const NOOP: PlayLaunchValue = { launch: () => {}, available: false };

const PlayLaunchContext = createContext<PlayLaunchValue>(NOOP);

export function PlayLaunchProvider({
  launch,
  children,
}: {
  launch: (opts?: PlayLaunchOptions) => void;
  children: ReactNode;
}) {
  return (
    <PlayLaunchContext.Provider value={{ launch, available: true }}>
      {children}
    </PlayLaunchContext.Provider>
  );
}

/** Read the Play launcher. Safe without a provider (returns a no-op launcher). */
export function usePlayLaunch(): PlayLaunchValue {
  return useContext(PlayLaunchContext);
}
